import React from 'react';

import { ApolloError, gql, useQuery } from '@apollo/client';

import eventBus from '@utils/eventBus';
import { Events } from 'src/global/constants';

import { imageCredentialsVar, wickedAppVar } from './../../apollo/reactiveVars';
import { ImageCredentials } from '../types';

interface QueryResult {
  getCredsForImages: ImageCredentials;
}

// GraphQL query
const GET_IMAGE_CREDENTIALS = gql`
  query getCredsForImages($wickedApp: String!, $wickedApi: String!) {
    getCredsForImages(wickedApp: $wickedApp, wickedApi: $wickedApi) {
      region
      bucket
      credentials {
        AccessKeyId
        SecretAccessKey
        SessionToken
        Expiration
      }
    }
  }
`;

const useImageCredentials = (wickedApi: string) => {
  // Execute query
  const { data, refetch } = useQuery<QueryResult>(GET_IMAGE_CREDENTIALS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    // Polling is used to refetch and update the image credentials before they're expired
    // Provided value in seconds
    pollInterval:
      (Number.parseInt(
        String(process.env.NEXT_PUBLIC_PRESIGN_FETCH_EXPIRY || '1800')
      ) -
        10) *
      1000,
    onError: (error: ApolloError) => {
      console.log(error.message);
    },
    variables: {
      wickedApp: wickedAppVar(),
      wickedApi: wickedApi,
    },
  });

  // Refetch image credentials
  const updateImageCredentials = () => {
    console.warn('Updating Image Credentials!');
    refetch();
  };

  // Fetch on mount
  React.useEffect(() => {
    // Note: manually refetching on initial load because due to issue in the library or implementation the credentials were not getting fetched on initial load
    refetch();

    // Handle image credentials expired event
    eventBus.on(Events.imageCredentialsExpired, updateImageCredentials);
    return () => {
      eventBus.off(Events.imageCredentialsExpired, updateImageCredentials);
    };
  }, []);

  // Update image credentials reactive var
  React.useEffect(() => {
    // Update credentials
    if (!data?.getCredsForImages) return;
    imageCredentialsVar(data.getCredsForImages);
  }, [data]);
};

export default useImageCredentials;
